import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TaskComments } from '@widgets/TaskComments';
import { FieldErrors } from 'entities/modal/model/types/modalFieldErrors';
import { updateCustomTask } from 'entities/task/api';
import { useDeleteTask } from 'entities/task/hooks/useDeleteTask';

import { ModalEditTaskView } from './ModalEditTaskView';
import { useModalEditTask } from './useModalEditTask';
import { useAppDispatch } from '../../../../store';
import { getUpdatedFieldErrors } from '../../lib/getUpdatedFieldErrors';
import {
    changeEditModalDescription,
    changeEditModalIsCompleted,
    changeEditModalTitle,
    resetEditModalData,
    setEditModalData,
} from '../../model/slice/editModalSlice';
import { TAPIError } from '../../model/types/apiError.type';

import type { IModalAttachment } from '@entities/attachment/model/IModalAttachment.interface';
import type { IAttachmentResponse } from 'entities/task/model/types/tasks';

// todo в модалках повторяется логика
// вынеси это в отдельный кастомный хук useModal,
// который будет возвращать хендлеры и управлять состоянием формы в модалке

export const ModalEditTask = ({ portalFileOverlays = false }: { portalFileOverlays?: boolean }) => {
    const dispatch = useAppDispatch();
    const {
        modalMode,
        onHideModal,
        pickedTaskUid,
        taskData,
        editModalData,
        isModalLoading,
        isObserver,
        tasksInformationData,
        previewEnable,
        setPreviewEnable,
    } = useModalEditTask();
    const { t } = useTranslation();

    const [fileIdToPreview, setFileIdToPreview] = useState(null);
    const [fileIdToDeleteFromList, setFileIdToDeleteFromList] = useState(null);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
    const [attachments, setAttachments] = useState<(IModalAttachment | IAttachmentResponse)[]>(() => {
        if (taskData) {
            return [...taskData.attachments];
        }
        return [];
    });

    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        title: { isError: false, errorMessage: null },
        description: { isError: false, errorMessage: null },
        objectOrEstimate: { isError: false, errorMessage: null },
        executor: { isError: false, errorMessage: null },
        tags: { isError: false, errorMessage: null },
        date: { isError: false, errorMessage: null },
    });
    const { deleteTaskHandler } = useDeleteTask({
        isModalLoading,
        taskData,
        pickedTaskUid,
        onHideModal,
    });
    const [triggerAnimationError, setTriggerAnimationError] = useState(false);
    const [deleteConfirmPopup, setDeleteConfirmPopup] = useState(false);

    const [modalData, setModalData] = useState({
        taskTitle: taskData?.title,
        taskDescription: taskData?.context?.description,
        date:
            (taskData?.date_start && taskData.date_start) ||
            (taskData?.date_end && taskData.date_end) ||
            '',
    });
    const [isCheckedRadio, setIsCheckedRadio] = useState(taskData?.completed);

    useEffect(() => {
        const tagsFormattedToEditStore = tasksInformationData.tags
            .map((tag) => (taskData?.context?.tags?.includes(tag.id) ? tag.title : null))
            .filter(Boolean);
        const formattedTaskData = {
            ...taskData,
            'date_end': taskData?.date_end === null ? '' : taskData?.date_end,
            'date_start': taskData?.date_start === null ? '' : taskData?.date_start,
            context: { ...taskData?.context, tags: tagsFormattedToEditStore },
        };

        if (taskData) {
            dispatch(setEditModalData(formattedTaskData));
        }
    }, [taskData, dispatch]);

    const deleteAttachment = (fileId: string | undefined) => {
        if (!fileId) return;
        setAttachments((prev) => prev.filter((attachment) => String(attachment.id) !== fileId));
        setFileIdToDeleteFromList(null);
        setDeleteConfirmModal(false);
    };

    const handleRadioBtn = () => {
        if (taskData?.title?.length === 0 || taskData?.context?.highest_role === 'observer') {
            return;
        }
        setIsCheckedRadio(!isCheckedRadio);
        dispatch(changeEditModalIsCompleted(!isCheckedRadio));
    };

    const clearFieldErrors = () => {
        setFieldErrors({
            title: { isError: false, errorMessage: null },
            description: { isError: false, errorMessage: null },
            objectOrEstimate: { isError: false, errorMessage: null },
            executor: { isError: false, errorMessage: null },
            tags: { isError: false, errorMessage: null },
            date: { isError: false, errorMessage: null },
        });
    };

    const onHidePrevModal = useCallback(() => {
        setPreviewEnable(false);
        setFileIdToPreview(null);
    }, []);
    const onEnablePreviewModal = useCallback((enablePreview: boolean, fileId: string) => {
        setPreviewEnable(enablePreview);
        setFileIdToPreview(fileId);
    }, []);

    const clearModalData = () => {
        setModalData(() => ({
            taskTitle: '',
            taskDescription: '',
            date: '',
        }));
    };

    const onChangeEditModalTitle = useCallback(
        (title: string) => {
            if (isCheckedRadio) return;
            if (fieldErrors.title.isError) {
                setFieldErrors((prev) => ({
                    ...prev,
                    title: { isError: false, errorMessage: null },
                }));
            }

            setModalData((prev) => ({ ...prev, taskTitle: title }));
            dispatch(changeEditModalTitle(title));
        },
        [fieldErrors.title, isCheckedRadio]
    );

    const onChangeModalDescription = useCallback(
        (description: string) => {
            if (isCheckedRadio) {
                return;
            }
            if (fieldErrors.description.isError) {
                setFieldErrors((prev) => ({
                    ...prev,
                    description: { isError: false, errorMessage: null },
                }));
            }
            setModalData((prev) => ({ ...prev, taskDescription: description }));
            dispatch(changeEditModalDescription(description));
        },
        [fieldErrors.description, isCheckedRadio]
    );

    const onSubmit = async () => {
        if (modalData.taskTitle.trim().length === 0) {
            setFieldErrors((prev) => ({
                ...prev,
                title: { isError: true, errorMessage: t('ModalEditTask.onSubmit.errorMessage') },
            }));
            setTriggerAnimationError(true);
            return;
        }
        if (
            fieldErrors.date.isError ||
            fieldErrors.tags.isError ||
            fieldErrors.title.isError ||
            fieldErrors.description.isError ||
            fieldErrors.executor.isError ||
            fieldErrors.objectOrEstimate.isError
        ) {
            console.error(t('ModalEditTask.onSubmit.errorMessage.console_error'));
            return;
        }
        try {
            clearFieldErrors();
            const updateTaskAction = dispatch(
                updateCustomTask({ ...editModalData, attachments } as never) as never
            ) as unknown as { unwrap: () => Promise<unknown> };

            await updateTaskAction.unwrap();
            clearModalData();

            onHideModal();
            dispatch(resetEditModalData());
        } catch (error) {
            if (error?.errors) {
                const apiErrors = error.errors as TAPIError[];
                const updatedFieldErrors = getUpdatedFieldErrors(fieldErrors, apiErrors);
                setFieldErrors(updatedFieldErrors);
            } else {
                console.error(t('ModalEditTask.onSubmit.unknownError'), error);
            }
        }
    };

    const switchDeleteConfirmPopup = () => {
        setDeleteConfirmPopup((prev) => !prev);
    };

    return (
        <ModalEditTaskView
            portalFileOverlays={portalFileOverlays}
            attachments={attachments}
            deleteConfirmModal={deleteConfirmModal}
            deleteConfirmPopup={deleteConfirmPopup}
            fieldErrors={fieldErrors}
            fileIdToDeleteFromList={fileIdToDeleteFromList}
            fileIdToPreview={fileIdToPreview}
            isCheckedRadio={isCheckedRadio}
            isModalLoading={isModalLoading}
            isObserver={isObserver}
            modalData={modalData}
            modalMode={modalMode}
            pickedTaskUid={pickedTaskUid}
            previewEnable={previewEnable}
            taskData={taskData}
            commentsPanel={
                pickedTaskUid &&
                modalMode === 'editTask' &&
                taskData?.type === 'custom' &&
                taskData.context.entity_uid ? (
                    <TaskComments key={pickedTaskUid} taskId={taskData.context.entity_uid} />
                ) : undefined
            }
            triggerAnimationError={triggerAnimationError}
            deleteAttachment={deleteAttachment}
            deleteTaskHandler={deleteTaskHandler}
            handleRadioBtn={handleRadioBtn}
            onChangeEditModalTitle={onChangeEditModalTitle}
            onChangeModalDescription={onChangeModalDescription}
            onEnablePreviewModal={onEnablePreviewModal}
            onHideModal={onHideModal}
            onHidePrevModal={onHidePrevModal}
            onSubmit={onSubmit}
            setAttachments={setAttachments}
            setDeleteConfirmModal={setDeleteConfirmModal}
            setFieldErrors={setFieldErrors}
            setFileIdToDeleteFromList={setFileIdToDeleteFromList}
            setTriggerAnimationError={setTriggerAnimationError}
            switchDeleteConfirmPopup={switchDeleteConfirmPopup}
        />
    );
};
